import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Renderer2,
  AfterViewInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { AuditItemDataSource } from '../audit-item.data.source';
import { AuditItemsService } from '../audit-items.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { AppService } from 'src/app/app.service';
import { RBACService } from 'src/app/shared/rbac.service';
import { tap } from 'rxjs/operators';
import { AuditItemModel } from '../audit-item.model';
import { AuditItemsTemplateEditComponent } from '../audit-items-template-edit/audit-items-template-edit.component';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { saveAs } from 'file-saver';
import { AuditItemsTemplateFilterComponent } from '../audit-items-template-filter/audit-items-template-filter.component';

@Component({
  selector: 'app-audit-items-template',
  templateUrl: './audit-items-template.component.html',
  styleUrls: ['./audit-items-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AuditItemsTemplateComponent implements OnInit, AfterViewInit {
  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new AuditItemDataSource(this.app, this.api);

  // Build displayedColumns dynamically: NOT as a static array.
  displayedColumns: string[] = [];
  sections: string[];
  selectedSection: string;
  query: string;

  @ViewChild('fileInput') fileInput: ElementRef;

  constructor(
    private auditItemService: AuditItemsService,
    private utils: UtilsService,
    public app: AppService,
    private dialog: MatDialog,
    public renderer: Renderer2,
    private api: ApiRequestService,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access inspection/audit questions.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = [
      'id',
      'name',
      'section',
      'date_created',
      'buttons'
    ];
    if (this.canView || this.canDelete) {
      this.displayedColumns.unshift('select');
    }

    // 4. Only load data if user has view permission.
    if (this.canView) {
      this.dataSource.template = true;
      this.dataSource.getData(false);
      this.auditItemService
        .getSections()
        .subscribe((response) => (this.sections = response.data));
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC columns by their corresponding date columns.
      if (this.sort.active == 'date_created_UTC') {
        this.dataSource.sort_by = 'date_created';
      }

      this.dataSource.getData(false);
    });
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  add() {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create inspection/audit questions.'
      );
      return;
    }
    this.openDialog(new AuditItemModel());
  }

  edit(item: AuditItemModel) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit inspection/audit questions.'
      );
      return;
    }
    this.openDialog(item);
  }

  private openDialog(item: AuditItemModel) {
    this.dialog
      .open(AuditItemsTemplateEditComponent, {
        width: '500px',
        data: { ...item }
      })
      .afterClosed()
      .subscribe((success) => {
        if (!success) {
          return;
        }
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  // filterSection() {
  //   this.dataSource.section = this.selectedSection;
  //   this.dataSource.getData(true);
  // }

  onRemoveSelected() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete inspection/audit questions.'
      );
      return;
    }
    const selected = this.dataSource.selection.selected;
    this.utils.showModal(
      'Remove Inspection/Audit Questions',
      `Are you sure you want to remove ${
        selected.length
      } Inspection/Audit questions?`,
      () => this.remove(selected)
    );
  }

  private remove(ids: number[]) {
    this.auditItemService.remove(ids).subscribe(() => {
      this.utils.showToast('Inspection/Audit questions have been removed.');
      this.dataSource.selection.clear();
      this.dataSource.getData(false);
    });
  }

  onRemove(item: AuditItemModel) {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete inspection/audit questions.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Inspection/Audit Question',
      `Are you sure you want to remove the "${
        item.name
      }" Inspection/Audit question?`,
      () => this.remove([item.id])
    );
  }

  onExportSelected(type: string = 'csv') {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view inspection/audit questions.'
      );
      return;
    }
    // Send a request to download audit items.
    this.api
      .makeDownloadRequest(
        `v1/audit-items/export/${type}`,
        {},
        {
          template: 1,
          order_by: this.dataSource.sort_by,
          order: this.dataSource.sort_order
        }
      )
      .then((response) => {
        const date = new Date();
        saveAs(
          response,
          `Inspection/Audit Questions - ${
            this.app.account.name
          } - ${date.getFullYear()}${date.getMonth() +
            1}${date.getDate()}.${type}`
        );
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  onTriggerFileSelector(evt: Event) {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create inspection/audit questions.'
      );
      return;
    }
    if (this.fileInput) {
      const clickEvt: MouseEvent = new MouseEvent('click', { bubbles: true });
      this.fileInput.nativeElement.dispatchEvent(clickEvt);
    }
  }

  onImport(evt: any) {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create inspection/audit questions.'
      );
      return;
    }
    if (evt.target.files.length > 0) {
      const files: any = [];
      for (let i = 0; i < evt.target.files.length; i++) {
        files.push(evt.target.files[i]);
      }

      this.utils.showModal(
        'Import Inspection/Audit Questions',
        'Are you sure you want to import the Inspection/Audit questions from the selected spreadsheet? It is recommended to use the same format as the Inspection/Audit questions exports. You will need to remove any duplicated audit items from the list after importing.',
        () => {
          const form = new FormData();
          files.forEach((file: any) => form.append(file.name, file));
          this.api
            .makeRequest('post', 'v1/audit-items/import', form, {}, {})
            .then((response: any) => {
              this.utils.showToast(
                'The Inspection/Audit questions import completed.'
              );
              if (this.canView) {
                this.dataSource.getData(true);
              }
            });
        }
      );

      // clear the target value
      evt.target.value = '';
    }
  }

  /**
   * Open the list filters.
   */
  onFilter() {
    this.utils
      .showComponentDialog(
        AuditItemsTemplateFilterComponent,
        {
          section: this.dataSource.section,
          sections: this.sections
        },
        {
          width: '500px'
        }
      )
      .then((response) => {
        if (response) {
          this.dataSource.section = response.section;
          this.dataSource.getData(true);
        }
      });
  }
}
